import { zip } from 'lodash';
import blocklistConfig from './resourceBlocklist.json';

// Re-exported for tests.
export const BLOCKLIST = blocklistConfig;

export type BlocklistFetchMode =
  // Never fetch the resource. (i.e., block it.)
  'ignore' |
  // Fetch the resource only in fallback mode.
  'localFallback' |
  // Fetch the resource through the resource proxy, but don't cache it.
  'proxy';
export type FetchMode =
  BlocklistFetchMode |
  // Fetch the resource through the resource proxy and cache it.
  'cache';

type Blocklist<Rule> = Record<BlocklistFetchMode, readonly Rule[]>;

// Valid url protocols for resource fetches.
const protocolWhiteList = new Set<string>(
  BLOCKLIST.protocolAllowlist.map(protocol => `${protocol}:`),
);

// Do not fetch any URL with the following prefixes.
const blockedUrlPrefixes: Blocklist<string> = BLOCKLIST.blockedUrlPrefixes;

// Do not fetch any domain with the following domain components.
const blockedDomains: Blocklist<string[]> = BLOCKLIST.blockedDomains;

// Strings that disqualify a URL if they appear *anywhere*.
const blockedSubstrings: Blocklist<string> = BLOCKLIST.blockedSubstrings;

// Don't fetch urls matching these regexps (sans protocol).
const blockedRegexps: Blocklist<RegExp> = {
  ignore: compilePatterns(BLOCKLIST.blockedRegexps.ignore),
  localFallback: compilePatterns(BLOCKLIST.blockedRegexps.localFallback),
  proxy: compilePatterns(BLOCKLIST.blockedRegexps.proxy),
};

// Cache for URL blocklist checks to avoid repeated expensive evaluations
const blocklistResultCache = new Map<string, FetchMode>();
const MAX_CACHE_SIZE = 10000; // Prevent unbounded growth
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds
let lastCacheClearTime = Date.now(); // Track when the cache was last cleared

/**
 * Given a resource URL, determine the {@link FetchMode} we should use to fetch
 * it; this determines whether we should fetch it at all, whether we should
 * fetch it through the resource proxy, and what kind of caching behavior should
 * be applied.
 *
 * The finer distinctions of the different {@link FetchMode} values are really
 * only useful for the resource proxy (which has its own implementation of this
 * function in 'fs/resources/blocklist.go') and for the playback service worker.
 * In other contexts, prefer {@link urlIsBlocklisted}, which provides a more
 * straightforward interface to the same information.
 *
 * NOTE: If you fix bugs or make behavioral changes to this code, update
 * 'fs/resources/blocklist.go' as well.
 *
 * @param url - A resource URL to check. This should be the original URL
 * without any kind of proxy or redirect wrapper.
 * @returns the {@link FetchMode} from the provided URL.
 */
export function urlFetchMode(url: URL): FetchMode {
  // Check if cache needs to be cleared (every 5 minutes)
  const now = Date.now();
  if (now - lastCacheClearTime >= CACHE_EXPIRATION_MS) {
    blocklistResultCache.clear();
    lastCacheClearTime = now;
  }

  // Check cache first
  const cacheKey = url.href;
  const cachedResult = blocklistResultCache.get(cacheKey);
  if (cachedResult !== undefined) {
    return cachedResult;
  }

  if (!protocolWhiteList.has(url.protocol)) {
    // Cache the result before returning
    if (blocklistResultCache.size < MAX_CACHE_SIZE) {
      blocklistResultCache.set(cacheKey, 'ignore');
    }
    return 'ignore';
  }

  // Below, we check each variety of blocklist in turn and early return if any
  // blocklist returns a fetch mode more restrictive than the most permissive
  // mode, 'cache'. (That mode means to fetch/proxy the resource and cache the
  // result.) We only use that mode if *all* blocklists return 'cache'.

  const testUrl = `${url.host}${url.pathname}${url.search}${url.hash}`;
  let mode = applyBlocklist(blockedUrlPrefixes, (prefixRule: string) => testUrl.startsWith(prefixRule));
  if (mode !== 'cache') {
    // Cache the result before returning
    if (blocklistResultCache.size < MAX_CACHE_SIZE) {
      blocklistResultCache.set(cacheKey, mode);
    }
    return mode;
  }

  const domainParts = url.hostname.split('.').map(part => part.toLowerCase());
  mode = applyBlocklist(blockedDomains, (blockedDomainParts: string[]) => {
    if (domainParts.length !== blockedDomainParts.length) { return false; }
    return zip(domainParts, blockedDomainParts).every(([part, blockedPart]) =>
      blockedPart === '*' || part === blockedPart,
    );
  });
  if (mode !== 'cache') {
    // Cache the result before returning
    if (blocklistResultCache.size < MAX_CACHE_SIZE) {
      blocklistResultCache.set(cacheKey, mode);
    }
    return mode;
  }

  mode = applyBlocklist(blockedSubstrings, (substring: string) => url.href.includes(substring));
  if (mode !== 'cache') {
    // Cache the result before returning
    if (blocklistResultCache.size < MAX_CACHE_SIZE) {
      blocklistResultCache.set(cacheKey, mode);
    }
    return mode;
  }

  // Compute the final result
  const result = applyBlocklist(blockedRegexps, (re: RegExp) => re.test(testUrl));

  // Cache the result before returning
  if (blocklistResultCache.size < MAX_CACHE_SIZE) {
    blocklistResultCache.set(cacheKey, result);
  }

  return result;
}

/**
 * @param url - A resource URL to check. This should be the original URL
 * without any kind of proxy or redirect wrapper.
 * @returns true if the provided URL is blocklist and should not be fetched.
 */
export function urlIsBlocklisted(url: URL): boolean {
  return urlFetchMode(url) === 'ignore';
}

function compilePatterns(patterns: string[]): RegExp[] {
  return patterns.map(re => new RegExp(re));
}

function applyBlocklist<Rule>(
  blocklist: Blocklist<Rule>,
  test: (rule: Rule) => boolean,
): FetchMode {
  for (const [fetchMode, rules] of Object.entries(blocklist)) {
    for (const rule of rules) {
      if (test(rule)) { return fetchMode as FetchMode; }
    }
  }
  return 'cache';
}
